/**
 * Chaves de workspace alinhadas ao xpro4api (workspaceConfigService) e ao monólito.
 * Salvamos em duplicidade identificadores legados + aliases passagens_* quando existirem,
 * para o Xpro antigo continuar lendo as mesmas chaves.
 */

/** JSON por parceiro: linhas virtuais Ativa/Inativa na listagem de integrações. */
export const PASSAGENS_UI_ATIVO_KEY = "passagens_ui_ativo";

/**
 * A API nunca devolve senha/token de ERP: no lugar vem este sentinela, que
 * informa "já configurado" sem revelar a credencial. Espelha
 * CONFIG_SECRET_SENTINEL em xpro4api/src/services/workspaceConfigService.js.
 */
export const CONFIG_SECRET_SENTINEL = "__xpro_secret_configured__";

const isSecretSentinel = (v) => String(v ?? "") === CONFIG_SECRET_SENTINEL;

/**
 * Espelha CONFIG_SECRET_KEY_RE do backend. Necessário no cliente porque cache
 * gravado antes da redação no servidor não traz o marcador `secret`.
 */
const CONFIG_SECRET_KEY_RE =
  /senha|password|passwd|token|api[_-]?key|apikey|secret|credential|private[_-]?key/i;

/** Chaves cujo `value` é JSON com credencial nas folhas. */
const CONFIG_SECRET_JSON_KEYS = new Set(["passagens_integracoes"]);

export function isSecretConfigKeyName(key) {
  const k = String(key || "");
  if (!k || CONFIG_SECRET_JSON_KEYS.has(k)) return false;
  return CONFIG_SECRET_KEY_RE.test(k);
}

export function isSecretJsonConfigKeyName(key) {
  return CONFIG_SECRET_JSON_KEYS.has(String(key || ""));
}

export const PASSAGENS_FETCH_KEYS = [
  "parceiropassagensrodoviarias",
  "passagens_integracoes",
  "hosttotalbus",
  "usuariototalbus",
  "nometotalbus",
  "senhatotalbus",
  "xTenantIdtotalbus",
  "idformapagamentototalbus",
  "nomeformapagamentototalbus",
  "passagens_totalbus_host",
  "passagens_totalbus_user",
  "passagens_totalbus_password",
  "passagens_totalbus_x_tenant_id",
  "passagens_totalbus_id_forma_pagamento",
  "passagens_totalbus_nome_forma_pagamento",
  "hostrodoviariaonline",
  "usernamerodoviariaonline",
  "passwordrodoviariaonline",
  "urlcidadesrodoviariaonline",
  "empresasrodoviariaonline",
  "blacklistcidadesrodoviariaonline",
  "passagens_rodoviariaonline_url",
  "passagens_rodoviariaonline_token",
  "hostrodosoft",
  "usuariorodosoft",
  "nomerodosoft",
  "senharodosoft",
  "codigoEmpresarodosoft",
  "passagens_rodosoft_url",
  "passagens_rodosoft_token",
  "hostpraxxio",
  "nomepraxxio",
  "empresapraxxio",
  "clientepraxxio",
  "senhapraxxio",
  "sistemapraxxio",
  "tipoBDpraxxio",
  "tipoAplicacaopraxxio",
  "bpepraxxio",
  "passagens_praxxio_url",
  "passagens_praxxio_token",
  "hostdistribusion",
  "apikeydistribusion",
  "marketingcarrieriddistribusion",
  "retailerpartnernumberdistribusion",
  "passagens_distribusion_url",
  "passagens_distribusion_token",
  "hostgopass",
  "usernamegopass",
  "passwordgopass",
  "passagens_gopass_url",
  "passagens_gopass_token",
  "hostbuson",
  "usernamebuson",
  "passwordbuson",
  "companybuson",
  "empresasbuson",
  "passagens_buson_url",
  "passagens_buson_token",

  PASSAGENS_UI_ATIVO_KEY,
  "passagens_modelo_bpe_fonte",
  "passagens_modelo_bpe_html",
  "passagens_modelo_voucher_fonte",
  "passagens_modelo_voucher_html",
  "passagens_modelo_logo",
];

const PASSAGENS_FETCH_KEYS_SET = new Set(PASSAGENS_FETCH_KEYS);

function parsePassagensIntegracoesJson(byKey) {
  const raw = byKey?.passagens_integracoes;
  if (!raw || typeof raw !== "string") return {};
  try {
    const o = JSON.parse(raw);
    return o && typeof o === "object" ? o : {};
  } catch {
    return {};
  }
}

function coalesce(...candidates) {
  for (const v of candidates) {
    if (v != null && String(v).trim() !== "" && !isSecretSentinel(v)) return String(v);
  }
  return "";
}

/**
 * Monta o mapa key→value só das chaves de passagens a partir de GET /configurations
 * (resposta completa ou filtrada), evitando query string enorme com ?keys=...
 */
export function passagensByKeyFromConfigurationsResponse(res) {
  const d = res?.data;
  const items = Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [];
  const byKey = {};
  for (const item of items) {
    const k = item?.key;
    if (k != null && PASSAGENS_FETCH_KEYS_SET.has(k)) byKey[k] = item.value;
  }
  return byKey;
}

/** Status 1/0 para linha virtual: default ativa (1) se não houver override gravado. */
export function getPassagensVirtualStatus(partner, byKey) {
  if (!partner || !byKey) return 1;
  const raw = byKey[PASSAGENS_UI_ATIVO_KEY];
  if (!raw || typeof raw !== "string") return 1;
  try {
    const map = JSON.parse(raw);
    if (!map || typeof map !== "object") return 1;
    if (!Object.prototype.hasOwnProperty.call(map, partner)) return 1;
    const v = map[partner];
    if (v === false || v === 0 || v === "0" || String(v).toLowerCase() === "false") return 0;
    return 1;
  } catch {
    return 1;
  }
}

/** Mescla um parceiro no mapa persistido em `passagens_ui_ativo`. */
export function mergePassagensUiAtivoMap(byKey, partner, nextActive) {
  const map = {};
  const raw = byKey?.[PASSAGENS_UI_ATIVO_KEY];
  if (raw && typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === "object") Object.assign(map, p);
    } catch {
      /* ignore */
    }
  }
  map[partner] = !!nextActive;
  return map;
}

/** Remove o parceiro do JSON persistido em `passagens_ui_ativo`. */
export function removePassagensPartnerFromUiAtivoMap(byKey, partner) {
  const map = {};
  const raw = byKey?.[PASSAGENS_UI_ATIVO_KEY];
  if (raw && typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === "object") Object.assign(map, p);
    } catch {
      /* ignore */
    }
  }
  if (partner) delete map[partner];
  return JSON.stringify(map);
}

/** Remove dados do parceiro em `passagens_integracoes` (evita credenciais só no JSON). */
export function stripPassagensIntegracoesForPartner(raw, partner) {
  let o = {};
  if (raw && typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === "object") o = { ...p };
    } catch {
      /* ignore */
    }
  }
  if (partner === "totalbus") {
    delete o.totalbus;
  } else if (partner) {
    if (!o.partners || typeof o.partners !== "object") o.partners = {};
    const pk = partner === "rodoviariaonline" ? "rodoviariaonline" : partner;
    delete o.partners[pk];
  }
  return JSON.stringify(o);
}

export function recordPassagensTestInStorage(companyKey, partner, ok, message) {
  if (typeof window === "undefined" || !partner) return;
  const storageKey = `passagens-test-status:${companyKey}`;
  let prev = {};
  try {
    prev = JSON.parse(localStorage.getItem(storageKey) || "{}") || {};
  } catch {
    prev = {};
  }
  const next = {
    ...prev,
    [partner]: {
      ok: !!ok,
      message: message || "",
      at: new Date().toISOString(),
    },
  };
  localStorage.setItem(storageKey, JSON.stringify(next));
}

const pick = (by, ...keys) => {
  for (const k of keys) {
    const v = by[k];
    // O sentinela conta como "existe", nunca como valor preenchível.
    if (v != null && String(v).trim() !== "" && !isSecretSentinel(v)) return String(v);
  }
  return "";
};

/**
 * Origem de cada campo secreto do formulário: as chaves soltas e o caminho
 * dentro de `passagens_integracoes`. Serve para saber se já existe credencial
 * guardada (flag `has_*`) sem nunca ter o valor em mãos.
 */
const PASSAGENS_SECRET_FIELD_SOURCES = {
  totalbus_password: {
    keys: ["senhatotalbus", "passagens_totalbus_password"],
    jsonPath: ["totalbus", "password"],
  },
  rodosoft_password: {
    keys: ["senharodosoft"],
    jsonPath: ["partners", "rodosoft", "password"],
  },
  praxxio_senha: {
    keys: ["senhapraxxio", "passagens_praxxio_token"],
    jsonPath: ["partners", "praxxio", "token"],
  },
  distribusion_apikey: {
    keys: ["apikeydistribusion", "passagens_distribusion_token"],
    jsonPath: ["partners", "distribusion", "token"],
  },
  gopass_password: {
    keys: ["passwordgopass"],
    jsonPath: ["partners", "gopass", "password"],
  },
  buson_password: {
    keys: ["passwordbuson"],
    jsonPath: ["partners", "buson", "password"],
  },
  rodo_password: {
    keys: ["passwordrodoviariaonline"],
    jsonPath: ["partners", "rodoviariaonline", "password"],
  },
};

/** Campos do formulário que guardam credencial (fonte única: `password: true`). */
export const PASSAGENS_SECRET_FORM_FIELDS = Object.freeze(
  Object.keys(PASSAGENS_SECRET_FIELD_SOURCES)
);

export const passagensSecretFlagKey = (field) => `has_${field}`;

function readJsonPath(json, path) {
  let node = json;
  for (const seg of path) {
    if (!node || typeof node !== "object") return undefined;
    node = node[seg];
  }
  return node;
}

const hasSomething = (v) => v != null && String(v).trim() !== "";

/** Já existe credencial guardada para este campo? Sentinela conta como sim. */
function secretFieldConfigured(field, byKey, json) {
  const src = PASSAGENS_SECRET_FIELD_SOURCES[field];
  if (!src) return false;
  if (src.keys.some((k) => hasSomething(byKey?.[k]))) return true;
  return hasSomething(readJsonPath(json, src.jsonPath));
}

export function createEmptyPassagensForm() {
  return {
    totalbus_host: "",
    totalbus_user: "",
    totalbus_password: "",
    totalbus_x_tenant_id: "",
    totalbus_id_forma_pagamento: "",
    totalbus_nome_forma_pagamento: "",
    rodosoft_host: "",
    rodosoft_user: "",
    rodosoft_password: "",
    rodosoft_codigo_empresa: "",
    praxxio_host: "",
    praxxio_nome: "",
    praxxio_senha: "",
    praxxio_empresa: "",
    praxxio_cliente: "",
    praxxio_sistema: "",
    praxxio_tipo_bd: "",
    praxxio_tipo_aplicacao: "",
    praxxio_bpe: true,
    distribusion_host: "",
    distribusion_apikey: "",
    distribusion_marketing_carrier_id: "",
    distribusion_retailer_partner_number: "",
    gopass_host: "",
    gopass_username: "",
    gopass_password: "",
    buson_host: "",
    buson_username: "",
    buson_password: "",
    buson_company: "",
    buson_empresas: "",
    rodo_host: "",
    rodo_username: "",
    rodo_password: "",
    rodo_empresas: "",
    rodo_url_cidades: "",
    rodo_blacklist_cidades: "",
    sync_partner_default: true,
    ticket_bpe_fonte: "padrao",
    ticket_bpe_html: "",
    ticket_voucher_fonte: "padrao",
    ticket_voucher_html: "",
    ticket_logo: "",
    // `has_<campo>`: existe credencial guardada no servidor para este campo.
    ...Object.fromEntries(
      PASSAGENS_SECRET_FORM_FIELDS.map((f) => [passagensSecretFlagKey(f), false])
    ),
  };
}

export function mergePassagensFormFromByKey(byKey) {
  const j = parsePassagensIntegracoesJson(byKey);
  const p = createEmptyPassagensForm();

  p.totalbus_host = coalesce(
    pick(byKey, "hosttotalbus", "passagens_totalbus_host"),
    j?.totalbus?.host
  );
  p.totalbus_user = coalesce(
    pick(byKey, "usuariototalbus", "passagens_totalbus_user"),
    pick(byKey, "nometotalbus"),
    j?.totalbus?.user
  );
  p.totalbus_password = coalesce(
    pick(byKey, "senhatotalbus", "passagens_totalbus_password"),
    j?.totalbus?.password
  );
  p.totalbus_x_tenant_id = coalesce(
    pick(byKey, "xTenantIdtotalbus", "passagens_totalbus_x_tenant_id"),
    j?.totalbus?.xTenantId
  );
  p.totalbus_id_forma_pagamento = coalesce(
    pick(byKey, "idformapagamentototalbus", "passagens_totalbus_id_forma_pagamento"),
    j?.totalbus?.idFormaPagamento
  );
  p.totalbus_nome_forma_pagamento = coalesce(
    pick(byKey, "nomeformapagamentototalbus", "passagens_totalbus_nome_forma_pagamento"),
    j?.totalbus?.nomeFormaPagamento
  );

  p.rodosoft_host = coalesce(
    pick(byKey, "hostrodosoft", "passagens_rodosoft_url"),
    j?.partners?.rodosoft?.url
  );
  p.rodosoft_user = coalesce(
    pick(byKey, "usuariorodosoft", "passagens_rodosoft_token"),
    pick(byKey, "nomerodosoft"),
    j?.partners?.rodosoft?.token
  );
  p.rodosoft_password = coalesce(
    pick(byKey, "senharodosoft"),
    j?.partners?.rodosoft?.password
  );
  p.rodosoft_codigo_empresa = coalesce(
    pick(byKey, "codigoEmpresarodosoft"),
    j?.partners?.rodosoft?.codigoEmpresa
  );

  p.praxxio_host = coalesce(
    pick(byKey, "hostpraxxio", "passagens_praxxio_url"),
    j?.partners?.praxxio?.url
  );
  p.praxxio_nome = coalesce(
    pick(byKey, "nomepraxxio"),
    j?.partners?.praxxio?.nome
  );
  p.praxxio_senha = coalesce(
    pick(byKey, "senhapraxxio", "passagens_praxxio_token"),
    j?.partners?.praxxio?.token
  );
  p.praxxio_empresa = coalesce(
    pick(byKey, "empresapraxxio"),
    j?.partners?.praxxio?.empresa
  );
  p.praxxio_cliente = coalesce(
    pick(byKey, "clientepraxxio"),
    j?.partners?.praxxio?.cliente
  );
  p.praxxio_sistema = coalesce(
    pick(byKey, "sistemapraxxio"),
    j?.partners?.praxxio?.sistema
  );
  p.praxxio_tipo_bd = coalesce(
    pick(byKey, "tipoBDpraxxio"),
    j?.partners?.praxxio?.tipoBD
  );
  p.praxxio_tipo_aplicacao = coalesce(
    pick(byKey, "tipoAplicacaopraxxio"),
    j?.partners?.praxxio?.tipoAplicacao
  );
  const bpeRaw = byKey.bpepraxxio ?? j?.partners?.praxxio?.bpe;
  p.praxxio_bpe =
    bpeRaw === undefined || bpeRaw === null || String(bpeRaw).trim() === ""
      ? true
      : !["0", "false", "n", "nao", "não"].includes(String(bpeRaw).trim().toLowerCase());

  p.distribusion_host = coalesce(
    pick(byKey, "hostdistribusion", "passagens_distribusion_url"),
    j?.partners?.distribusion?.url
  );
  p.distribusion_apikey = coalesce(
    pick(byKey, "apikeydistribusion", "passagens_distribusion_token"),
    j?.partners?.distribusion?.token
  );
  p.distribusion_marketing_carrier_id = coalesce(
    pick(byKey, "marketingcarrieriddistribusion"),
    j?.partners?.distribusion?.marketingCarrierId
  );
  p.distribusion_retailer_partner_number = coalesce(
    pick(byKey, "retailerpartnernumberdistribusion"),
    j?.partners?.distribusion?.retailerPartnerNumber
  );

  p.gopass_host = coalesce(
    pick(byKey, "hostgopass", "passagens_gopass_url"),
    j?.partners?.gopass?.url
  );
  p.gopass_username = coalesce(
    pick(byKey, "usernamegopass", "passagens_gopass_token"),
    j?.partners?.gopass?.token
  );
  p.gopass_password = coalesce(
    pick(byKey, "passwordgopass"),
    j?.partners?.gopass?.password
  );

  p.buson_host = coalesce(
    pick(byKey, "hostbuson", "passagens_buson_url"),
    j?.partners?.buson?.url
  );
  p.buson_username = coalesce(
    pick(byKey, "usernamebuson", "passagens_buson_token"),
    j?.partners?.buson?.token
  );
  p.buson_password = coalesce(
    pick(byKey, "passwordbuson"),
    j?.partners?.buson?.password
  );
  p.buson_company = coalesce(
    pick(byKey, "companybuson"),
    j?.partners?.buson?.company
  );
  p.buson_empresas = coalesce(
    pick(byKey, "empresasbuson"),
    j?.partners?.buson?.empresas
  );

  p.rodo_host = coalesce(
    pick(byKey, "hostrodoviariaonline", "passagens_rodoviariaonline_url"),
    j?.partners?.rodoviariaonline?.url
  );
  p.rodo_username = coalesce(
    pick(byKey, "usernamerodoviariaonline", "passagens_rodoviariaonline_token"),
    j?.partners?.rodoviariaonline?.token
  );
  p.rodo_password = coalesce(
    pick(byKey, "passwordrodoviariaonline"),
    j?.partners?.rodoviariaonline?.password
  );
  p.rodo_empresas = coalesce(
    pick(byKey, "empresasrodoviariaonline"),
    j?.partners?.rodoviariaonline?.empresas
  );
  p.rodo_url_cidades = coalesce(
    pick(byKey, "urlcidadesrodoviariaonline"),
    j?.partners?.rodoviariaonline?.urlCidades
  );
  p.rodo_blacklist_cidades = coalesce(
    pick(byKey, "blacklistcidadesrodoviariaonline"),
    j?.partners?.rodoviariaonline?.blacklistCidades
  );

  const fonteOf = (raw) => {
    const s = String(raw || "").trim().toLowerCase();
    return ["proprio", "custom", "1", "true", "sim", "html"].includes(s) ? "proprio" : "padrao";
  };
  p.ticket_bpe_fonte = fonteOf(byKey.passagens_modelo_bpe_fonte);
  p.ticket_bpe_html = byKey.passagens_modelo_bpe_html || "";
  p.ticket_voucher_fonte = fonteOf(byKey.passagens_modelo_voucher_fonte);
  p.ticket_voucher_html = byKey.passagens_modelo_voucher_html || "";
  p.ticket_logo = byKey.passagens_modelo_logo || "";

  // Campo secreto nunca é pré-preenchido: fica vazio com a flag `has_*` ligada
  // para a UI mascarar e o save preservar o valor guardado.
  for (const field of PASSAGENS_SECRET_FORM_FIELDS) {
    p[field] = "";
    p[passagensSecretFlagKey(field)] = secretFieldConfigured(field, byKey, j);
  }

  return p;
}

function bpeToLegacy(v) {
  return v ? "1" : "0";
}

/**
 * Na Praxio o toggle BPe/TFO escolhe o documento. Nos demais parceiros o
 * passageiro recebe BPe, então só o modelo fiscal entra na tela.
 */
export function visiblePassagensTicketKind(partner, form = {}) {
  if (String(partner || "").trim().toLowerCase() === "praxxio" && form.praxxio_bpe === false) {
    return "voucher";
  }
  return "bpe";
}

/**
 * Pares [identificador, value, options] para PUT — dual-write legado + alias.
 *
 * Campo secreto em branco com credencial já guardada é **omitido** do save:
 * enviá-lo vazio apagaria a senha do ERP. `opts.clearSecrets` inverte isso e
 * marca `clearSecret` para o servidor aceitar a limpeza (fluxo de exclusão).
 */
export function buildPassagensSavePairs(partner, pf, opts = {}) {
  const out = [];
  const clearSecrets = !!opts.clearSecrets;

  const pushDual = (legacyKey, aliasKey, value) => {
    const s = value == null ? "" : String(value);
    if (legacyKey) out.push([legacyKey, s]);
    if (aliasKey) out.push([aliasKey, s]);
  };

  const pushSecret = (formField, legacyKey, aliasKey) => {
    const raw = pf?.[formField];
    const typed = raw == null || isSecretSentinel(raw) ? "" : String(raw);
    if (clearSecrets) {
      const options = { clearSecret: true };
      if (legacyKey) out.push([legacyKey, "", options]);
      if (aliasKey) out.push([aliasKey, "", options]);
      return;
    }
    // Mascarado e intocado: preserva o que está no servidor.
    if (!typed.trim() && pf?.[passagensSecretFlagKey(formField)]) return;
    if (legacyKey) out.push([legacyKey, typed]);
    if (aliasKey) out.push([aliasKey, typed]);
  };

  if (partner === "totalbus") {
    pushDual("hosttotalbus", "passagens_totalbus_host", pf.totalbus_host);
    pushDual("usuariototalbus", "passagens_totalbus_user", pf.totalbus_user);
    out.push(["nometotalbus", pf.totalbus_user == null ? "" : String(pf.totalbus_user)]);
    pushSecret("totalbus_password", "senhatotalbus", "passagens_totalbus_password");
    pushDual("xTenantIdtotalbus", "passagens_totalbus_x_tenant_id", pf.totalbus_x_tenant_id);
    pushDual(
      "idformapagamentototalbus",
      "passagens_totalbus_id_forma_pagamento",
      pf.totalbus_id_forma_pagamento
    );
    pushDual(
      "nomeformapagamentototalbus",
      "passagens_totalbus_nome_forma_pagamento",
      pf.totalbus_nome_forma_pagamento
    );
  } else if (partner === "rodosoft") {
    pushDual("hostrodosoft", "passagens_rodosoft_url", pf.rodosoft_host);
    pushDual("usuariorodosoft", "passagens_rodosoft_token", pf.rodosoft_user);
    out.push(["nomerodosoft", pf.rodosoft_user == null ? "" : String(pf.rodosoft_user)]);
    pushSecret("rodosoft_password", "senharodosoft", null);
    out.push([
      "codigoEmpresarodosoft",
      pf.rodosoft_codigo_empresa == null ? "" : String(pf.rodosoft_codigo_empresa),
    ]);
  } else if (partner === "praxxio") {
    pushDual("hostpraxxio", "passagens_praxxio_url", pf.praxxio_host);
    out.push(["nomepraxxio", pf.praxxio_nome == null ? "" : String(pf.praxxio_nome)]);
    pushSecret("praxxio_senha", "senhapraxxio", "passagens_praxxio_token");
    out.push(["empresapraxxio", pf.praxxio_empresa == null ? "" : String(pf.praxxio_empresa)]);
    out.push(["clientepraxxio", pf.praxxio_cliente == null ? "" : String(pf.praxxio_cliente)]);
    out.push(["sistemapraxxio", pf.praxxio_sistema == null ? "" : String(pf.praxxio_sistema)]);
    out.push(["tipoBDpraxxio", pf.praxxio_tipo_bd == null ? "" : String(pf.praxxio_tipo_bd)]);
    out.push([
      "tipoAplicacaopraxxio",
      pf.praxxio_tipo_aplicacao == null ? "" : String(pf.praxxio_tipo_aplicacao),
    ]);
    out.push(["bpepraxxio", bpeToLegacy(!!pf.praxxio_bpe)]);
  } else if (partner === "distribusion") {
    pushDual("hostdistribusion", "passagens_distribusion_url", pf.distribusion_host);
    pushSecret("distribusion_apikey", "apikeydistribusion", "passagens_distribusion_token");
    out.push([
      "marketingcarrieriddistribusion",
      pf.distribusion_marketing_carrier_id == null ? "" : String(pf.distribusion_marketing_carrier_id),
    ]);
    out.push([
      "retailerpartnernumberdistribusion",
      pf.distribusion_retailer_partner_number == null
        ? ""
        : String(pf.distribusion_retailer_partner_number),
    ]);
  } else if (partner === "gopass") {
    pushDual("hostgopass", "passagens_gopass_url", pf.gopass_host);
    pushDual("usernamegopass", "passagens_gopass_token", pf.gopass_username);
    pushSecret("gopass_password", "passwordgopass", null);
  } else if (partner === "buson") {
    pushDual("hostbuson", "passagens_buson_url", pf.buson_host);
    pushDual("usernamebuson", "passagens_buson_token", pf.buson_username);
    pushSecret("buson_password", "passwordbuson", null);
    out.push(["companybuson", pf.buson_company == null ? "" : String(pf.buson_company)]);
    out.push(["empresasbuson", pf.buson_empresas == null ? "" : String(pf.buson_empresas)]);
  } else if (partner === "rodoviariaonline") {
    pushDual("hostrodoviariaonline", "passagens_rodoviariaonline_url", pf.rodo_host);
    pushDual("usernamerodoviariaonline", "passagens_rodoviariaonline_token", pf.rodo_username);
    pushSecret("rodo_password", "passwordrodoviariaonline", null);
    out.push(["empresasrodoviariaonline", pf.rodo_empresas == null ? "" : String(pf.rodo_empresas)]);
    out.push([
      "urlcidadesrodoviariaonline",
      pf.rodo_url_cidades == null ? "" : String(pf.rodo_url_cidades),
    ]);
    out.push([
      "blacklistcidadesrodoviariaonline",
      pf.rodo_blacklist_cidades == null ? "" : String(pf.rodo_blacklist_cidades),
    ]);
  } else {
    return [];
  }

  if (pf.sync_partner_default) {
    out.push(["parceiropassagensrodoviarias", partner]);
  }

  const ticketFonte = (v) => (v === "proprio" ? "proprio" : "padrao");
  out.push(["passagens_modelo_bpe_fonte", ticketFonte(pf.ticket_bpe_fonte)]);
  out.push(["passagens_modelo_voucher_fonte", ticketFonte(pf.ticket_voucher_fonte)]);
  if (ticketFonte(pf.ticket_bpe_fonte) === "proprio") {
    out.push(["passagens_modelo_bpe_html", pf.ticket_bpe_html == null ? "" : String(pf.ticket_bpe_html)]);
  }
  if (ticketFonte(pf.ticket_voucher_fonte) === "proprio") {
    out.push(["passagens_modelo_voucher_html", pf.ticket_voucher_html == null ? "" : String(pf.ticket_voucher_html)]);
  }
  out.push(["passagens_modelo_logo", pf.ticket_logo == null ? "" : String(pf.ticket_logo)]);

  return out;
}

/**
 * Pares [chave, valor] para limpar credenciais do parceiro no workspace (ex.: excluir linha virtual).
 */
export function buildPassagensDeletePuts(byKey, partner) {
  if (!partner) return [];
  const empty = {
    ...createEmptyPassagensForm(),
    sync_partner_default: false,
    praxxio_bpe: false,
  };
  // clearSecrets: aqui o branco é intencional, então o servidor precisa aceitar
  // apagar a credencial em vez de preservá-la.
  const pairs = [...buildPassagensSavePairs(partner, empty, { clearSecrets: true })].filter(
    ([key]) => !String(key).startsWith("passagens_modelo_")
  );
  const curParceiro = String(byKey?.parceiropassagensrodoviarias ?? "").trim();
  if (curParceiro === partner) {
    pairs.push(["parceiropassagensrodoviarias", ""]);
  }
  const nextJson = stripPassagensIntegracoesForPartner(byKey?.passagens_integracoes, partner);
  const prevJson = typeof byKey?.passagens_integracoes === "string" ? byKey.passagens_integracoes : "";
  if (nextJson !== prevJson) {
    pairs.push(["passagens_integracoes", nextJson]);
  }
  pairs.push([PASSAGENS_UI_ATIVO_KEY, removePassagensPartnerFromUiAtivoMap(byKey, partner)]);
  return pairs;
}

/** Campos por parceiro. `advanced: true` fica oculto até o usuário abrir “Mais opções”. */
export const PASSAGENS_UI_TEXT_FIELDS = {
  totalbus: [
    { key: "totalbus_host", label: "Host (URL da API)" },
    { key: "totalbus_user", label: "Usuário" },
    { key: "totalbus_password", label: "Senha", password: true },
    { key: "totalbus_x_tenant_id", label: "X Tenant ID" },
    {
      key: "totalbus_id_forma_pagamento",
      label: "ID forma de pagamento",
      advanced: true,
    },
    {
      key: "totalbus_nome_forma_pagamento",
      label: "Nome forma de pagamento",
      advanced: true,
    },
  ],
  rodosoft: [
    { key: "rodosoft_host", label: "Host" },
    { key: "rodosoft_user", label: "Usuário" },
    { key: "rodosoft_password", label: "Senha", password: true },
    { key: "rodosoft_codigo_empresa", label: "Código da empresa" },
  ],
  praxxio: [
    { key: "praxxio_host", label: "Host (servidor Autumn)" },
    { key: "praxxio_nome", label: "Nome (login)" },
    { key: "praxxio_senha", label: "Senha", password: true },
    { key: "praxxio_empresa", label: "Empresa", advanced: true },
    { key: "praxxio_cliente", label: "Cliente", advanced: true },
    { key: "praxxio_sistema", label: "Sistema", advanced: true },
    { key: "praxxio_tipo_bd", label: "Tipo BD", advanced: true },
    { key: "praxxio_tipo_aplicacao", label: "Tipo aplicação", advanced: true },
  ],
  distribusion: [
    { key: "distribusion_host", label: "Host (API)" },
    { key: "distribusion_apikey", label: "API Key", password: true },
    {
      key: "distribusion_marketing_carrier_id",
      label: "Marketing carrier ID(s)",
      advanced: true,
    },
    {
      key: "distribusion_retailer_partner_number",
      label: "Retailer partner number",
      advanced: true,
    },
  ],
  gopass: [
    { key: "gopass_host", label: "Host" },
    { key: "gopass_username", label: "Usuário" },
    { key: "gopass_password", label: "Senha", password: true },
  ],
  buson: [
    { key: "buson_host", label: "Host" },
    { key: "buson_username", label: "Usuário" },
    { key: "buson_password", label: "Senha", password: true },
    { key: "buson_company", label: "Company (header)", advanced: true },
    { key: "buson_empresas", label: "Empresas (códigos, separadas por vírgula)", advanced: true },
  ],
  rodoviariaonline: [
    { key: "rodo_host", label: "Host da API" },
    { key: "rodo_username", label: "Usuário" },
    { key: "rodo_password", label: "Senha", password: true },
    { key: "rodo_empresas", label: "Nomes das empresas (vírgula)" },
    { key: "rodo_url_cidades", label: "URL arquivo de cidades", advanced: true },
    { key: "rodo_blacklist_cidades", label: "Códigos de cidade (filtro, vírgula)", advanced: true },
  ],
};

export function passagensUiSplitFields(partner) {
  const all = PASSAGENS_UI_TEXT_FIELDS[partner] || [];
  const primary = all.filter((f) => !f.advanced);
  const advanced = all.filter((f) => f.advanced);
  return { primary, advanced, hasAdvanced: advanced.length > 0 };
}

/**
 * Indica se o workspace tem credenciais mínimas para o parceiro (linha virtual + UX).
 * Regras alinhadas a `Helpers_PassagensRodoviarias::partner` e construtores em `library/Helpers/*.php`.
 */
export function passagensPartnerConfiguredInByKey(partner, byKey) {
  if (!partner || !byKey) return false;
  const p = mergePassagensFormFromByKey(byKey);
  const t = (v) => String(v ?? "").trim();
  // Credencial nunca chega em texto claro: a existência vem da flag `has_*`.
  const hasSecret = (field) => !!p[passagensSecretFlagKey(field)] || !!t(p[field]);

  if (partner === "totalbus") {
    // Legado PHP aceita x-tenant-id vazio no curl; não exigir para “configurado”.
    return !!(t(p.totalbus_host) && t(p.totalbus_user) && hasSecret("totalbus_password"));
  }
  if (partner === "rodosoft") {
    return !!(t(p.rodosoft_host) && t(p.rodosoft_user) && hasSecret("rodosoft_password"));
  }
  if (partner === "praxxio") {
    return !!(t(p.praxxio_host) && (hasSecret("praxxio_senha") || t(p.praxxio_nome)));
  }
  if (partner === "distribusion") {
    // Legado: só `apikeydistribusion` define o parceiro; host default no PHP se vazio.
    return hasSecret("distribusion_apikey");
  }
  if (partner === "gopass") {
    // Legado: partner() exige usuário + senha; host pode ser preenchido depois.
    return !!(t(p.gopass_username) && hasSecret("gopass_password"));
  }
  if (partner === "buson") {
    // Legado: partner() exige usuário + senha; host default no PHP (hmg-...) se vazio.
    return !!(t(p.buson_username) && hasSecret("buson_password"));
  }
  if (partner === "rodoviariaonline") {
    return !!(
      (t(p.rodo_username) && hasSecret("rodo_password")) ||
      (t(p.rodo_host) && t(p.rodo_empresas))
    );
  }
  return false;
}

/** Parceiros de venda de passagens (ERP) suportados. */
export const PASSAGENS_PARTNERS = Object.keys(PASSAGENS_UI_TEXT_FIELDS);

/**
 * Indica se o workspace tem ao menos uma integração de venda de passagens (ERP)
 * configurada — usado para só exibir a venda de passagens no chat quando houver ERP.
 */
export function hasAnyPassagensPartnerConfigured(byKey) {
  if (!byKey) return false;
  return PASSAGENS_PARTNERS.some((partner) => passagensPartnerConfiguredInByKey(partner, byKey));
}

